import { useMemo } from "react";
import Customer from "../../../models/Customer";
import { useContracts } from "../../../networking/contract.hooks";
import { useAllCustomers } from "../../../networking/customer.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";

export interface JobFilterOptions {
	customers: { id: string; name: string }[];
	contracts: { id: string; name: string }[];
	pools: { id: string; name: string }[];
	/** org `jobTagTypes` — descriptive job tags (key + resolved Swedish name) */
	tags: { id: string; name: string }[];
	/** org `customerTagTypes` — tags carried by customers (key + resolved Swedish name) */
	customerTags: { id: string; name: string }[];
	/** org `consultantTagTypes` — tags a job can REQUIRE (key + resolved Swedish name) */
	requirementTags: { id: string; name: string }[];
	/** org `certificationTypes` — certifications a job can REQUIRE (key + resolved Swedish name) */
	requirementCertifications: { id: string; name: string }[];
}

/**
 * Bundles the option lists the job filter UI needs — customers, contracts and
 * pools available in the organisation. Mirrors `useCustomerFilterData`: shared
 * by the jobs-list filter drawer and the org-defined-filters settings editor so
 * both offer the exact same options. Pool names come from the org's pool list;
 * contract names from the contracts endpoint.
 */
export function useJobFilterData(pool: string = "") {
	const { customers, isLoading } = useAllCustomers(pool);
	const { data: organisation } = useOrganisation();
	// Large page so the whole set is available for labelling/selection.
	const { contracts: contractList } = useContracts({ pageSize: 10000 });

	const options = useMemo<JobFilterOptions>(() => {
		const customerOptions = ((customers as Customer[]) ?? [])
			.filter((c) => !!c.id)
			.map((c) => ({ id: c.id as string, name: c.name || (c.id as string) }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		const contractOptions = (contractList ?? [])
			.map((c) => ({ id: c.id, name: c.name || c.id }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		const poolOptions = (organisation?.pools ?? [])
			.map((p) => ({ id: p.key, name: p.name || p.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		// Straight from the org taxonomy (like pools) so tags are filterable
		// before any job carries them.
		const tagOptions = (organisation?.jobTagTypes ?? [])
			.map((tag) => ({ id: tag.key, name: tag.name?.sv || tag.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		// Same idea for customer tags: the org taxonomy decides whether the
		// section exists at all (no types → the builder hides it).
		const customerTagOptions = (organisation?.customerTagTypes ?? [])
			.map((tag) => ({ id: tag.key, name: tag.name?.sv || tag.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		// Kompetenskrav options: the taxonomies a job's matching gate references.
		// All types are offered (not only list-view ones) — `showInListViews` is a
		// display concern for the column, not a filter restriction.
		const requirementTagOptions = (organisation?.consultantTagTypes ?? [])
			.map((tag) => ({ id: tag.key, name: tag.name?.sv || tag.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		const requirementCertificationOptions = (organisation?.certificationTypes ?? [])
			.map((cert) => ({ id: cert.key, name: cert.name?.sv || cert.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		return {
			customers: customerOptions,
			contracts: contractOptions,
			pools: poolOptions,
			tags: tagOptions,
			customerTags: customerTagOptions,
			requirementTags: requirementTagOptions,
			requirementCertifications: requirementCertificationOptions,
		};
	}, [customers, contractList, organisation]);

	return { options, isLoading };
}
